import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Approach", path: "/approach" },
  { label: "Portfolio", path: "/portfolio" },
  { label: "For Founders", path: "/founders" },
  { label: "For Investors", path: "/investors" },
  { label: "Team", path: "/team" },
  { label: "Insights", path: "/insights" },
  { label: "Contact", path: "/contact" },
];

export default function HeaderNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="header-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-obsidian/85 backdrop-blur-xl border-b border-white/5" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-2 group shrink-0">
          <span className="font-extrabold tracking-tight text-lg sm:text-xl text-white leading-none">
            Anvaya
          </span>
          <span className="font-mono text-[10px] sm:text-xs tracking-[0.35em] text-champagne group-hover:text-champagneBright transition-colors duration-300">
            PARTNERS
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-5" data-testid="nav-desktop">
          {LINKS.slice(1).map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-xs font-medium tracking-wide whitespace-nowrap transition-colors duration-300 ${
                  isActive ? "text-champagne" : "text-gray-400 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <Link
            to="/founders"
            data-testid="nav-cta-apply"
            className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-champagne text-obsidian text-xs font-bold tracking-wide px-5 py-2.5 hover:bg-champagneBright hover:gap-2.5 transition-all duration-300"
          >
            APPLY AS FOUNDER <ArrowUpRight size={14} />
          </Link>
          <button
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-menu-button"
            className="xl:hidden text-gray-200 p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden bg-obsidian/95 backdrop-blur-xl border-b border-white/5"
            data-testid="nav-mobile-drawer"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.path}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                >
                  <NavLink
                    to={l.path}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `text-lg font-semibold ${isActive ? "text-champagne" : "text-gray-300"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
